import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { authFetch } from "../../utils/auth";
import DiagnosticMapWindow from "./DiagnosticMapWindow";
import scopeSnapshotToSource, { isolateSnapshot } from "./scopeGraphToSource";
import { resolveSourceMapKey, titleFromFileName } from "./sourceMapLink";

/**
 * SourceMapPage — standalone full-page viewer for a diagnostic map scoped to
 * one or more source documents. Opened in a new browser tab from the causal
 * chip on a Copilot source card.
 *
 * Session scoping was removed because isolateSnapshot() strips every
 * session-dependent field (visited_nodes, traversal_path, current_node_nid,
 * etc.), so the session id contributes NOTHING to what this page renders.
 * Removing it means one shareable URL and one hot server cache key per
 * product + file, instead of one per session.
 *
 * Data resolution chain (each step used only if the previous yields nothing):
 * 1. localStorage handoff (fast, no network) via the `k` query param.
 * 2. Product-wide fetch: `/api/knowledge-graph/<product>` — every runbook the
 *    product owns is included, then scopeSnapshotToSource narrows to the
 *    requested file(s).
 * 3. Empty state, which SAYS WHICH failure it was. Some sources still reach the
 *    frontend with an empty file_name, and "no graph" and "could not match" are
 *    different answers to the engineer: one means the document has no runbook,
 *    the other means we hold a graph and this document is not in it. A blank
 *    canvas, or one honest-sounding sentence covering both, hides a real bug.
 */

// Same endpoint construction as useDiagMap.js (product-wide form only) —
// kept in sync manually. Session scoping is not used here; the inline
// dashboard map (useDiagMap.js) still uses session_id for the live overlay.
const KNOWLEDGE_GRAPH_ENDPOINT = (product) =>
  `/api/knowledge-graph/${encodeURIComponent(product)}`;

// Module-level so the prop keeps a stable identity across renders — the
// window's layout effect depends on `trail`, and a fresh [] every render
// would re-run the dagre layout on every state change.
const EMPTY_TRAIL = [];

// Why the viewer has nothing to draw. Each one is a DIFFERENT fact about the
// system, so each gets its own sentence rather than a shared "not found".
const FAILURES = {
  no_name: {
    headline: "This source has no document name.",
    detail:
      "The card reached the browser without a file name, so there is nothing to look "
      + "up in the diagnostic graph. This is a backend gap, not an empty runbook.",
  },
  no_product: {
    headline: "No product for this session.",
    detail:
      "The diagnostic graph is fetched per product, and this session has not "
      + "identified one yet. Ask a question in the session first, then reopen this map.",
  },
  fetch_failed: {
    headline: "Could not load the diagnostic graph.",
    detail:
      "The knowledge-graph request failed or returned no graph for this product. "
      + "This says nothing about whether the document has a runbook — retry, or check "
      + "that the product still has mermaid runbooks configured.",
  },
  empty_graph: {
    headline: "This product has no diagnostic graph.",
    detail:
      "The graph loaded, but it contains no nodes at all — no runbook in this product "
      + "has a flowchart to show.",
  },
  no_match: {
    headline: "No diagnostic graph for this document.",
    detail:
      "The product's graph loaded and was searched, including the "
      + "“Runbook - ” prefix and extension-insensitive forms, and no node cites "
      + "this document. Other runbooks in the product do have flowcharts — this one does not.",
  },
};

export default function SourceMapPage() {
  // sessionId is optional — present only on legacy /source-map/:sessionId URLs.
  // It is NOT used for fetching or keying; kept only so those routes don't 404.
  const { sessionId: _legacySessionId } = useParams(); // eslint-disable-line no-unused-vars
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const productParam = params.get("product") || "";
  // Raw, possibly absent: the link omits `title` whenever it is just the first
  // file name minus its extension. Kept separate from the DISPLAY title below
  // because the match fallback must not fall back to a derived value.
  const rawTitleParam = params.get("title") || "";

  // All `file` params — aggregate cards emit one per contributing runbook.
  // Falls back to the title so links shared before the multi-name change
  // (which carried a single `file` param or none at all) still resolve:
  // normaliseName reduces both "Runbook - X.pdf" and "Runbook - X" to the
  // same form, so the title recovers the match rather than faking one.
  const fileParams = useMemo(
    () => params.getAll("file").filter(Boolean),
    [params]
  );
  const matchNames = useMemo(
    () => (fileParams.length ? fileParams : [rawTitleParam].filter(Boolean)),
    [fileParams, rawTitleParam]
  );
  // Display title: the param when it carries something the file name does not
  // (aggregate cards are named for the group, not for any one document),
  // otherwise the first file name with its extension stripped.
  const titleParam =
    rawTitleParam || titleFromFileName(fileParams[0]) || "Diagnostic Map";

  // localStorage handoff key. Product-keyed (v2): the key is rebuilt from the
  // product param plus the slug of the first `file`, which is exactly what the
  // parent tab wrote. `k` reappears only when the parent keyed the entry on a
  // card_key (aggregate cards), and legacy links that carried the WHOLE key
  // are still honoured verbatim.
  const lsKey = useMemo(
    () => resolveSourceMapKey(productParam, params.get("k"), fileParams[0]),
    [productParam, params, fileParams]
  );
  // Stable primitive for the effect dep array — an array literal is a new
  // reference every render and would re-trigger the fetch in an infinite loop.
  const matchKey = useMemo(() => matchNames.join("\0"), [matchNames]);

  const [snap, setSnap] = useState(null);
  const [title, setTitle] = useState(titleParam);
  const [loading, setLoading] = useState(true);
  // null while resolving / on success; one of the FAILURES keys otherwise.
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      // 1. Try localStorage handoff — already scoped and isolated by the
      //    parent tab's handleOpenSourceMap. Use only if snapshot is non-null.
      //    Belt-and-suspenders: re-isolate in case a stale entry from before
      //    the isolation fix is still cached, or the parent wrote pre-fix data.
      if (lsKey) {
        try {
          const raw = localStorage.getItem(lsKey);
          if (raw) {
            const data = JSON.parse(raw);
            if (data?.snapshot) {
              if (!cancelled) {
                setSnap(isolateSnapshot(data.snapshot));
                if (data.title) setTitle(data.title);
                setLoading(false);
              }
              return;
            }
          }
        } catch (_) {
          // parse failed — fall through to fetch
        }
      }

      // The upstream defect this viewer has to survive: a source can arrive with
      // no file_name at all. The card hides its button in that case, but a link
      // can still be opened by hand, and "no name" must not be reported as
      // "this document has no runbook".
      if (!matchNames.length) {
        if (!cancelled) { setLoading(false); setFailure("no_name"); }
        return;
      }
      if (!productParam) {
        if (!cancelled) { setLoading(false); setFailure("no_product"); }
        return;
      }

      // 2. Product-wide fetch — every runbook the product owns is included.
      //    scopeSnapshotToSource then narrows to the requested file(s).
      let graphNodeCount = null;
      try {
        const url = KNOWLEDGE_GRAPH_ENDPOINT(productParam);
        const res = await authFetch(url);
        if (!cancelled && res?.ok) {
          const json = await res.json();
          graphNodeCount = json?.diagnostic_subgraph?.nodes?.length || 0;
          const scoped = scopeSnapshotToSource(json, matchNames);
          if (scoped) {
            setSnap(scoped);
            setLoading(false);
            return;
          }
        }
      } catch (_) {
        // fetch failed — fall through to empty state
      }

      // 3. The fetch did not produce a match. Name the reason.
      if (!cancelled) {
        setLoading(false);
        if (graphNodeCount === null) setFailure("fetch_failed");
        else if (graphNodeCount === 0) setFailure("empty_graph");
        else setFailure("no_match");
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [lsKey, matchKey, productParam]); // eslint-disable-line react-hooks/exhaustive-deps -- matchNames is derived from matchKey; using the array would loop

  // Set the browser tab title so the engineer can identify it in the tab bar.
  useEffect(() => {
    document.title = title || "Diagnostic Map";
  }, [title]);

  // Clean empty state when data could not be resolved. Names the document it
  // was asked about AND which of the failure modes happened.
  if (!loading && failure) {
    const { headline, detail } = FAILURES[failure] || FAILURES.no_match;
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#FAFAF6]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        <div className="text-center max-w-md px-6">
          <p className="text-lg font-bold text-[#253439] mb-2">{title}</p>
          <p className="text-sm font-semibold text-[#475569] mb-2">{headline}</p>
          <p className="text-[12px] text-gray-500 leading-relaxed">{detail}</p>
          {matchNames.length > 0 && (
            <p className="text-[11px] text-gray-400 mt-3 break-words">
              Looked for: {matchNames.join(", ")}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen">
      <DiagnosticMapWindow
        standalone
        hideTrace
        open
        minimized={false}
        snapshot={snap}
        fullSnapshot={snap}
        trail={EMPTY_TRAIL}
        title={title || "Diagnostic Map"}
        isGraphLoading={loading}
      />
    </div>
  );
}
